import { CircularProgress, Stack } from "@mui/material";

import { DefaultPageLayout } from "features/core/DefaultPageLayout";
import { GlobalErrorMessage } from "features/core/GlobalErrorMessage";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { useEffect, useState } from "react";
import { caseViewerSelectors } from "./store/selectors";
import { Sidebar } from "./Sidebar";
import { caseViewerThunks } from "./store/slice";
import { EditDossierDialog } from "../caseManagement/EditDossierDialog";
import { selectCaseManagerConfiguration } from "features/config/store/selectors";
import { Playback } from "./Playback";
import { useSignalRGroupSubscriber } from "features/messages/hooks/useSignalRGroup";
import { ObjectDetectionInsightsDialog } from "./reId/ObjectDetectionInsightsDialog";
import { reIdSelectors } from "./reId/store/selectors";
import { reIdActions } from "./reId/store/slice";
import { ReIdObjectDetectionCollector } from "./reId/ReIdObjectDetectionCollector";

export interface DossierProps {
    drawerWidth: number;
}

export function Dossier(props: DossierProps) {
    const dispatch = useAppDispatch();

    // configuration
    const caseManagerConfiguration = useAppSelector(
        selectCaseManagerConfiguration,
    );

    // load dossier based on id from url param
    const params = useParams();

    useEffect(() => {
        if (params.dossierId) {
            dispatch(
                caseViewerThunks.loadDossierAsync({
                    dossierId: params.dossierId,
                }),
            );
        }
    }, [dispatch, params.dossierId]);

    // subscribe to SignalR updates of this dossier
    useSignalRGroupSubscriber(params?.dossierId);

    // selectors
    const dossier = useAppSelector(caseViewerSelectors.selectDossier);
    const errorLoadingDossier = useAppSelector(
        caseViewerSelectors.selectErrorLoadingDossier,
    );
    const isLoadingDossier = useAppSelector(
        caseViewerSelectors.selectIsLoadingDossier,
    );
    const selectSelectedDetectedObjectFrame = useAppSelector(
        reIdSelectors.selectSelectedDetectedObjectFrame,
    );

    // component state
    const [showEditDossier, setShowEditDossier] = useState(false);

    return (
        <>
            <DefaultPageLayout
                drawerWidth={props.drawerWidth}
                hideHeaderBarInFullscreen={true}
                hideSideBarInFullscreen={true}
                sidebar={
                    dossier && (
                        <Sidebar
                            dossier={dossier}
                            onEditDossier={() => setShowEditDossier(true)}
                        />
                    )
                }
                main={
                    <Stack spacing={3}>
                        {isLoadingDossier && (
                            <CircularProgress title="Loading dossier..." />
                        )}

                        <Playback
                            dossier={dossier}
                            caseManagerConfiguration={caseManagerConfiguration}
                            drawerWidth={props.drawerWidth}
                        />

                        {errorLoadingDossier && (
                            <GlobalErrorMessage
                                title={"Unable to fetch dossier"}
                                message={"Please contact the service desk."}
                                debugData={{ errorLoadingDossier }}
                                alertSeverity={"error"}
                            />
                        )}
                    </Stack>
                }
            />
            {dossier && showEditDossier && (
                <EditDossierDialog
                    dossier={dossier}
                    closeDialog={() => {
                        dispatch(
                            caseViewerThunks.loadDossierAsync({
                                dossierId: dossier.id,
                            }),
                        );

                        setShowEditDossier(false);
                    }}
                />
            )}
            {dossier && selectSelectedDetectedObjectFrame && (
                <ObjectDetectionInsightsDialog
                    dossierId={dossier.id}
                    selectedObjectDetectionFrame={
                        selectSelectedDetectedObjectFrame
                    }
                    closeDialog={() => {
                        dispatch(
                            reIdActions.setSelectedDetectedObjectFrame(
                                undefined,
                            ),
                        );
                    }}
                />
            )}
            <ReIdObjectDetectionCollector />
        </>
    );
}
